import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAppState } from "../context/AppStateContext";
import { findEngineerMaster, isEngineerLinkageComplete } from "../utils/engineer";

export function EngineerAssignModal({
  open,
  applicationId,
  onClose,
}: {
  open: boolean;
  applicationId: string | null;
  onClose: () => void;
}) {
  const { getById, engineerMasters, updateEngineer, finalizeEngineer } = useAppState();
  const [linkError, setLinkError] = useState<string | null>(null);

  const app = applicationId ? getById(applicationId) : undefined;

  useEffect(() => {
    if (!open) setLinkError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !applicationId || !app) return null;

  const link = app.engineer;
  const selectedMaster = findEngineerMaster(engineerMasters, link.engineerMasterId);

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal modal--assign-subcontractor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="eng-assign-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="eng-assign-modal-title">Assign engineer</h2>
        <p className="modal__lede">
          <span className="table-name">{app.mobile.beneficiaryName}</span>
          <span className="text-muted" aria-hidden="true">
            {" "}
            ·{" "}
          </span>
          <span className="mono text-muted">{app.mobile.applicationId}</span>
        </p>

        {linkError ? (
          <p className="alert alert--danger" role="alert">
            {linkError}
          </p>
        ) : null}

        <h3 className="form-section-title">Engineer master</h3>
        <div className="field field--full">
          <label htmlFor="eng-assign-master-select">Onboarded engineer</label>
          <select
            id="eng-assign-master-select"
            value={link.engineerMasterId ?? ""}
            onChange={(e) => {
              setLinkError(null);
              const v = e.target.value;
              updateEngineer(app.id, {
                engineerMasterId: v === "" ? null : v,
              });
            }}
          >
            <option value="">— Select an engineer —</option>
            {engineerMasters.map((m) => (
              <option key={m.id} value={m.id}>
                {m.fullName}
                {m.phone ? ` · ${m.phone}` : ""}
              </option>
            ))}
          </select>
          <p className="text-muted" style={{ marginTop: "0.35rem", fontSize: "0.8125rem" }}>
            Missing someone?{" "}
            <Link to="/engineer-onboarding" className="btn btn--link btn--sm" style={{ padding: 0, verticalAlign: "baseline" }}>
              Engineer onboarding
            </Link>
          </p>
        </div>

        {selectedMaster ? (
          <div className="subcontractor-firm-panel subcontractor-firm-panel--compact">
            <h4 className="form-section-title">Selected engineer (read-only)</h4>
            <dl className="summary-dl">
              <dt>Phone</dt>
              <dd>{selectedMaster.phone || "—"}</dd>
              <dt>Email</dt>
              <dd>{String(selectedMaster.email ?? "").trim() || "—"}</dd>
              <dt>Employee code</dt>
              <dd className="mono">{String(selectedMaster.employeeCode ?? "").trim() || "—"}</dd>
            </dl>
          </div>
        ) : null}

        <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: 0 }}>
          Use{" "}
          <Link to={`/applications/${app.id}/engineer`} state={{ from: "beneficiaries" }}>
            Open dossier — Engineer tab
          </Link>{" "}
          for the full screen. Click below to record the assignment once an engineer is selected.
        </p>

        <div className="modal__actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary btn--tg"
            onClick={async () => {
              setLinkError(null);
              const master = findEngineerMaster(engineerMasters, link.engineerMasterId);
              if (!isEngineerLinkageComplete(link, master)) {
                setLinkError(
                  "Choose an engineer from the master list (name and phone required). Register them under Engineer onboarding first if they are not listed.",
                );
                return;
              }
              const ok = await finalizeEngineer(app.id);
              if (ok) onClose();
            }}
          >
            {link.linkedAt ? "Update assignment" : "Record engineer"}
          </button>
        </div>
      </div>
    </div>
  );
}
